// apps/landing-page/src/components/personalization/PersonalizationLauncher.tsx
'use client';

import { useCallback, useEffect, useState } from 'react';
import { usePreferences, type AudienceSegment } from '@/context/PreferencesContext';
import { useSegmentPolicy } from '@/hooks/useSegmentPolicy';
import { hasSeenGreeting, markGreetingSeen } from '@/lib/prefs-storage';
import { PersonalizationChip } from './PersonalizationChip';
import { PersonalizationModal } from './PersonalizationModal';

// How long to wait after mount before the first-visit greeting can open, so
// the page paints first instead of the modal appearing to block load.
const GREETING_DELAY_MS = 800;

// A slim persistent chip plus a modal carrying the segment/interest picker.
//
// The modal used to auto-open when the catalog scrolled into view. It no longer
// does: it interrupted a first-time guest with a backdrop, and what it buys them is
// catalog de-noising, which is a nice-to-have rather than a setup step. The chip is
// the entry point now, so personalizing is opt-in.
//
// One deliberate exception: a true first-time visitor (never greeted, no saved
// prefs) still gets a single, one-time welcome open — see the effect below. It
// is gated by the `aks_greeted` cookie so it can only ever fire once per browser.
// See docs/personalization-spec.md.
export function PersonalizationLauncher() {
  const { segment, interests, setSegment, setInterests, toggleInterest, clearPreferences } = usePreferences();
  const { examTypes, status: policyStatus } = useSegmentPolicy(segment);
  const [isOpen, setIsOpen] = useState(false);
  const [isGreeting, setIsGreeting] = useState(false);

  // First-visit greeting. `segment`/`interests` start empty on the server and on
  // the very first client render, then flip to the real cookie-backed values once
  // PreferencesContext hydrates — so this effect is deliberately keyed on both:
  // it re-evaluates (and the pending timer below is cancelled by the cleanup) the
  // moment hydration reveals the visitor already has preferences, instead of
  // firing on stale pre-hydration defaults.
  useEffect(() => {
    if (hasSeenGreeting()) return undefined;
    if (segment || interests.length > 0) return undefined;

    const timer = setTimeout(() => {
      markGreetingSeen();
      setIsGreeting(true);
      setIsOpen(true);
    }, GREETING_DELAY_MS);

    return () => clearTimeout(timer);
  }, [segment, interests]);

  const handleOpen = useCallback(() => {
    setIsGreeting(false);
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSegmentChange = useCallback((next: AudienceSegment) => setSegment(next), [setSegment]);

  // Chip toggles update selection live but must NOT close the modal —
  // only Simpan, Lewati, or an explicit close does that.
  const handleToggleInterest = useCallback((slug: string) => toggleInterest(slug), [toggleInterest]);

  const handleClearInterests = useCallback(() => setInterests([]), [setInterests]);

  const handleSave = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleClear = useCallback(() => {
    clearPreferences();
  }, [clearPreferences]);

  return (
    <>
      <PersonalizationChip segment={segment} onClick={handleOpen} />
      <PersonalizationModal
        open={isOpen}
        segment={segment}
        interests={interests}
        examTypes={examTypes}
        policyStatus={policyStatus}
        greeting={isGreeting}
        onClose={handleClose}
        onSegmentChange={handleSegmentChange}
        onToggleInterest={handleToggleInterest}
        onClearInterests={handleClearInterests}
        onSave={handleSave}
        onClear={handleClear}
      />
    </>
  );
}
